import { AxiosError } from 'axios';

const FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  password: 'Password',
  password1: 'Password',
  password2: 'Password confirmation',
  full_name: 'Name',
  confirm: '',
  non_field_errors: '',
  detail: '',
  title: 'Title',
  name: 'Name',
  task_name: 'Task name',
  time_logged: 'Duration',
  entry_date: 'Date',
  due_date: 'Due date',
  category: 'Category',
  priority: 'Priority',
  message: 'Message',
  // The AI chat endpoint returns {error, code}; show the error text with no
  // label prefix and never surface the machine-readable code to the user.
  error: '',
};

// Keys carrying machine-readable metadata, not user-facing text.
const IGNORED_KEYS = new Set(['code', 'period', 'resets_at', 'usage']);

/**
 * Turn a DRF error response into a single human-readable message.
 * DRF errors come as {detail: "..."}, {non_field_errors: [...]} or
 * {field: ["msg", ...], ...}.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const axiosError = error as AxiosError<unknown>;

  if (axiosError?.isAxiosError) {
    if (!axiosError.response) {
      return 'Cannot reach the server. Check your connection and try again.';
    }
    const data = axiosError.response.data;

    if (typeof data === 'string' && data.trim()) {
      // HTML error pages are useless to users — fall through to the fallback.
      if (!data.trimStart().startsWith('<')) return data;
    } else if (data && typeof data === 'object') {
      const messages: string[] = [];
      for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
        if (IGNORED_KEYS.has(key)) continue;
        const label = FIELD_LABELS[key] ?? key;
        const texts = (Array.isArray(value) ? value : [value])
          .filter((v) => typeof v === 'string')
          .join(' ');
        if (texts) {
          messages.push(label ? `${label}: ${texts}` : texts);
        }
      }
      if (messages.length) return messages.join('\n');
    }

    if (axiosError.response.status === 401) return 'Your session has expired. Please sign in again.';
    if (axiosError.response.status === 403) return 'You do not have permission to do that.';
    if (axiosError.response.status >= 500) return 'The server ran into a problem. Please try again shortly.';
  }

  return fallback;
}
